/**
 * Client HTTP verso le API di Portman.
 *
 * Concentra in un punto solo la gestione degli errori, così ogni schermata
 * riceve un `ApiError` con codice e messaggio già pronti da mostrare, invece di
 * dover interpretare ogni volta la risposta.
 */

export class ApiError extends Error {
  override readonly name = "ApiError";
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  /** Chiave di catalogo: l'interfaccia la traduce nella propria lingua (vedi `testoErrore`). */
  readonly key: string | null;
  readonly params: Record<string, unknown> | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: unknown,
    key: string | null = null,
    params?: Record<string, unknown>,
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
    this.key = key;
    this.params = params;
  }

  /** La sessione è scaduta o assente. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

const BASE = "/api";

interface ErrorBody {
  error?: { code?: string; message?: string; details?: unknown; key?: string; params?: Record<string, unknown> };
}

export async function toApiError(response: Response): Promise<ApiError> {
  const body = (await response.json().catch(() => null)) as ErrorBody | null;
  const error = body?.error;

  if (!error?.key && !error?.message) {
    return new ApiError(response.status, "HTTP_ERROR", "", error?.details, "errori.http", {
      status: response.status,
    });
  }

  return new ApiError(
    response.status,
    error.code ?? "HTTP_ERROR",
    error.message ?? "",
    error.details,
    error.key ?? null,
    error.params,
  );
}

export interface ApiClientOptions {
  readonly fetch?: typeof fetch;
}

export function createApiClient({ fetch: fetchImpl = fetch }: ApiClientOptions = {}) {
  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    let response: Response;

    try {
      response = await fetchImpl(`${BASE}${path}`, {
        method,
        // Il cookie di sessione è httpOnly: senza questo non verrebbe inviato.
        credentials: "same-origin",
        ...(body === undefined
          ? {}
          : { headers: { "content-type": "application/json" }, body: JSON.stringify(body) }),
      });
    } catch {
      throw new ApiError(0, "NETWORK_ERROR", "", undefined, "errori.rete");
    }

    if (!response.ok) throw await toApiError(response);

    // 204 e risposte vuote non hanno un corpo da interpretare.
    if (response.status === 204) return null as T;

    return (await response.json().catch(() => null)) as T;
  }

  return {
    get: <T>(path: string) => request<T>("GET", path),
    post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
    put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
    patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
    delete: <T>(path: string) => request<T>("DELETE", path),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

export const api = createApiClient();
